/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable react/no-array-index-key */
import React, {
  useRef, useCallback, useContext, useEffect, useState, useMemo
} from 'react';
import PropTypes, { string, number, bool, object } from 'prop-types';
import { BrowseSearchRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import {
  client,
  extend,
  QueryProvider,
  QueryContext,
  hoist,
  useLazyDataModel,
  params,
  bool as boolType,
  shape as shapeType,
  string as stringType,
  number as numberType,
  alias,
  arrayOf
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';
import { AisleBay, AisleBayPodsDataProvider, AisleBayPodsContext } from '@thd-olt-component-react/aisle-bay';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { CategoryCardsMobile } from '@thd-olt-component-react/category-cards';
import { CompareTray } from '@thd-olt-component-react/compare';
import { EmtQuestionsAndAnswers, BrandCategories } from '@thd-olt-component-react/emt-generated-content';
import { Skeleton, SkeletonLine, SkeletonBlock } from '@one-thd/sui-atomic-components';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { HybridContentfulWidgets, useHybridPLP } from '@thd-olt-component-react/hybrid';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { Metadata, BrowseSearchMetadata } from '@thd-olt-component-react/metadata';
import { NoResultsFound } from '@thd-olt-component-react/no-results-found';
import { ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';
import { FlooringDrawer } from '@thd-olt-component-react/flooring-picker';

import {
  ProductPodGroup,
  CustomProductPodDetails
} from '@thd-olt-component-react/product-pod-group';
import {
  ProductResults,
  ResultsApplied,
  ResultsFilterAndSort,
  ResultsFilterAndSortDrawer,
  ResultsHeader,
  ResultsPagination,
  ResultsWrapped,
  ResultsDimensions,
  useProductResults,
  productDefaultResultsDataModel,
  updateQueryParams,
  parseUrl,
  storeLifecycleEvent,
  ProductResultsContext
} from '@thd-olt-component-react/product-results';
import { OrderSample, usePLPSamples } from '@thd-olt-component-react/order-sample';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { RentalIntentMobile } from '@thd-olt-component-react/rental-intent';
import { useNavigate, useLocation } from '@thd-olt-component-react/router';
import { SponsoredBanner, SponsoredInGridBanner } from '@thd-olt-component-react/sponsored-content';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { TextAnExpert } from '@thd-olt-component-react/text-an-expert';
import {
  DiscoveryZonesWrapper,
  PlpRecsRenderPlaceholder,
  PlpRvRenderPlaceholder,
  BuyItAgain
} from '@thd-olt-component-react/thd-recs-containers';
import { TrackOrder } from '@thd-olt-component-react/track-order';
import { ProductCategoryVisnav } from '@thd-olt-component-react/product-category-visnav';
import { ConnectedTranslatorWidget } from '@thd-olt-component-react/translator_widget';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import {
  EmtGeneratedContentLoad, EmtGeneratedContent, EmtGeneratedContentModel,
  EmtLinksLoad, EmtLinks, EmtLinksModel,
} from './dynamic-imports';
import {
  getBrowseAndSearchItemIds,
  limitProductsToDisplay,
  impProductPodData,
  impPLPRecs,
  imagePreloader,
  isRefinedPage,
  isNIncluded
} from './browse-and-search-utils';
import {
  CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER,
  KEY_PRODUCT_FEATURE_POD_CATEGORIES,
  HYDRATION_STANDARD_SCROLL_BUFFER,
  RECS_REQUEST_KEY, CHECK_CAT_STYLE,
  BRAND_PAGE, HYBRID_LAYOUT
} from '../../constants';
import { CustomProductPodResponsive } from './CustomProductPodResponsive';

const HoistedProductPodGroup = hoist(
  ProductPodGroup, CustomProductPodResponsive, CategoryCardsMobile, CustomProductPodDetails
);
export const BrowseAndSearchMobile = (props) => {
  declareContexts([ProductResultsContext, QueryContext, ExperienceContext, AisleBayPodsContext, CustomerContext]);
  const navigate = useNavigate();
  const thdCustomer = useThdCustomer();
  const { isCustomerIdentified = false } = useThdCustomer() || {};
  const {
    clientStore,
    cookie,
    isServer,
    deliveryZip: serverDeliveryZip,
    isConsumerApp,
    channel,
    dataType,
    customer
  } = useContext(ExperienceContext);
  const { search: querystring } = useLocation();
  const {
    pageType,
    prefetchPip,
    productPodHoverDelay,
    pathname,
    customerType,
    instance,
  } = props;
  const path = `${pathname}${querystring}`;
  const checkShowProduct = CHECK_CAT_STYLE.test(path);
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const prefetchedSkuSet = useRef(new Set());
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const { storeId, storeZip, isLocalized } = store;
  const pageLoadVariables = useRef(null);
  const isNoResultsFoundRef = useRef(false);
  const SERVER_SIDE_PRODUCT_POD_RENDER_COUNT = 12;
  const SERVER_SIDE_PRODUCT_POD_FETCH_COUNT = isSearch ? 24 : 12;
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 24;
  const searchNavPropsRef = useRef({
    pageSize: disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_FETCH_COUNT,
    askForSponsored: isSearch,
    deliveryZip: serverDeliveryZip,
  });
  const theme = useTheme(BrowseAndSearchMobile);
  const { hideSponsoredCarousel, hideFFMFilters } = theme.props;
  let THDCustomer = {};
  if (clientStore) {
    let deliveryZipCode;
    if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails) {
      deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    searchNavPropsRef.current = {
      pageSize: CLIENT_SIDE_PRODUCT_POD_COUNT,
      askForSponsored: true,
      deliveryZip: deliveryZipCode?.zipcode,
    };
    THDCustomer = typeof window !== 'undefined' ? window.THDCustomer?.default : {};
  }
  const HEADER_OFFSET = 59;

  const enablePlccPlpComponent = useConfigService('fs:enablePlccPlpComponent');

  const isDiscoveryZonesEnabled = useConfigService('fs-prop:isDiscoveryZonesEnabled');
  const isDiscoveryZonesSearchEnabled = useConfigService('fs-prop:isDiscoveryZonesSearchEnabled');
  const viewPrimaryFilters = true;
  const isFulfillmentRedesignEnabled = useConfigService('fs-prop:isFulfillmentRedesignEnabled');
  const viewVizNavFilters = useConfigService('fs-prop:isViewVizNavFiltersEnabled');
  const isGetItFastOnPlp = true;
  const filterBrowseRecs = useConfigService('fs:browseFilteredRecs');
  const isInGridBannerEnabled = useConfigService('fs-prop:isInGridBannerEnabled') || false;
  // Feature Switch that enables hiding or showing pickup and delivery messaging based on filter selection.
  const isFulfillmentFSEnabled = useConfigService('fs:isFulfillmentFilterAndMsgEnabled');
  const swapPrimaryFiltersGetItFastEnabled = useConfigService('fs:swapPrimaryFiltersGetItFastEnabled');

  // Combine these flags into a single condition we can check more easily.
  const isDiscoveryZonesEligible = (isBrowse && isDiscoveryZonesEnabled) || (isSearch && isDiscoveryZonesSearchEnabled);
  const fsisViewSimilarButtonPositionAboveABTest = useConfigService('fs:isViewSimilarButtonPositionAboveABTest')
      || false;
  const fsisViewSimilarButtonPositionBelowABTest = useConfigService('fs:isViewSimilarButtonPositionBelowABTest')
      || false;
  const showHideViewSimilarIcon = (
    fsisViewSimilarButtonPositionAboveABTest
      || fsisViewSimilarButtonPositionBelowABTest
  ) || false;

  const [inGridBannerIndex, setInGridBannerIndex] = useState(null);

  const { isClientResolved, setDefaultVariables } = useContext(QueryContext);

  const onStoreChange = ({ output }) => {
    const { storeId: updatedStoreId } = output || {};
    if (!updatedStoreId) return;

    if (window && window.THD_LOCALIZER_AUTO_INIT && window.THD_LOCALIZER_AUTO_INIT.Localizer) {
      window.THD_LOCALIZER_AUTO_INIT.Localizer
        .localize({ storeId: updatedStoreId })
        .then(() => {
          window.location.reload();
        });
    }
  };

  const positionRef = useRef({ lastScrollPosition: 0 });

  // eslint-disable-next-line consistent-return
  const handleScroll = useCallback(() => {
    const header = document.querySelector('.Header3');
    if (!header) return null;
    const { lastScrollPosition } = positionRef.current;
    const scrollPosition = window?.pageYOffset || document?.documentElement?.scrollTop;
    if (scrollPosition > lastScrollPosition && scrollPosition > HEADER_OFFSET) {
      header.classList.add('Header3__header--hide');
    } else {
      header.classList.remove('Header3__header--hide');
    }
    positionRef.current.lastScrollPosition = scrollPosition <= 0 ? 0 : scrollPosition;
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
  }, []);

  const onDimensionsChange = useCallback(({ refinement }) => {
    if (!viewPrimaryFilters) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);
    } else if (refinement?.dimensionName === 'Get It Fast') {
      storeLifecycleEvent('filters-refinements-visualNav.click', refinement);
    }
    navigate(refinement.url);
  });

  const resultsDimensionsRef = useRef(null);
  const onPageChange = useCallback(({ url }) => {
    navigate(url);

    const { offsetTop } = resultsDimensionsRef?.current?.parentElement?.parentElement;
    window.scrollTo(0, offsetTop || 0);
  });

  const onSortChange = useCallback(({
    sortby, sortorder, name, currentPage, page, updatedPath = null
  }) => {
    const newPath = updateQueryParams(updatedPath || path, [
      {
        paramName: 'Nao',
        paramValue: null
      },
      {
        paramName: 'sortorder',
        paramValue: sortorder
      },
      {
        paramName: 'sortby',
        paramValue: sortby
      }
    ]);
    window.LIFE_CYCLE_EVENT_BUS.trigger('sort-refinements.click', {
      name,
      currentPage,
      page
    });
    navigate(newPath);
  });

  const cleanPath = path.replace(/^\/render[^/]*/, '');

  const hideOnNoCategory = () => {
    return path && (path.match('/s/') || /Ntt-/.test(path));
  };

  const isEtchVisNavOnPLP = (data) => {
    try {
      const stringifiedPartialTemplates = data?.searchModel?.partialTemplates;
      const etchVisualNavExists = (stringifiedPartialTemplates?.[0] || '').indexOf('visualNavigation') > -1;
      const hasPLPBanner = data?.searchModel?.metadata?.hasPLPBanner;
      const isPLPPage = data?.searchModel?.metadata?.contentType === 'plppage';
      return hasPLPBanner && etchVisualNavExists && isPLPPage;
    } catch (err) {
      return false;
    }
  };

  const {
    keyword,
    lowerbound,
    Nao = 0,
    sortby,
    nValue = '',
    sortorder,
    upperbound,
    skipSpellCheck
  } = parseUrl({ path: cleanPath });

  // For hybrid-contentful-plp
  const shouldSkipHybridPlp = isSearch || isRefinedPage(pathname);
  const hybridParams = {
    slug: isNIncluded(nValue) ? nValue : 'N-' + nValue,
    customerType,
    instance,
    storeId,
    skip: shouldSkipHybridPlp,
    lazy: shouldSkipHybridPlp,
    isShowProducts: checkShowProduct
  };
  const { data: hybridData, contentElements, loading: hybridLoading } = useHybridPLP(hybridParams);
  useHelmet('general-merchandise', { hybridData }, imagePreloader, [hybridData]);
  const layouts = hybridData?.layouts;
  const layoutID = layouts?.id; // for impressions
  const isHybridLayout = layoutID && layouts?.content?.__typename === HYBRID_LAYOUT; // for validation
  const isBrandPage = layouts?.content?.pageType === BRAND_PAGE;
  const localizedAndClientLoaded = useRef(false);
  const loadedStore = useRef(store);
  const showKeyProductFeaturePods = KEY_PRODUCT_FEATURE_POD_CATEGORIES.find((value) => nValue.includes(value));
  const timeoutRef = useRef(null);
  const [productPageChunkLoaded, setProductPageChunkLoaded] = useState(false);
  const additionalSearchParams = {};
  const hasSponsoredSorts = !sortby || ['bestmatch', 'topsellers'].indexOf(sortby) > -1;
  if (searchNavPropsRef.current.askForSponsored && hasSponsoredSorts && cookie?.adobeCookie?.MCMID) {
    additionalSearchParams.sponsored = true;
    additionalSearchParams.mcvisId = cookie.adobeCookie.MCMID;
  }
  if (searchNavPropsRef.current.deliveryZip) {
    additionalSearchParams.deliveryZip = searchNavPropsRef.current.deliveryZip;
  }
  if (checkShowProduct) {
    additionalSearchParams.plp = true;
  }
  if (skipSpellCheck) {
    additionalSearchParams.skipSpellCheck = skipSpellCheck;
  }
  // only populated for non-cacheable pages (e.g search)
  if (customer?.svocId) {
    additionalSearchParams.svocID = customer?.svocId;
  }
  if (Object.keys(THDCustomer).length !== 0 && THDCustomer.svocID) {
    additionalSearchParams.svocID = THDCustomer.svocID;
  }

  const opts = {
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    pageSize: searchNavPropsRef.current.pageSize,
    storeId,
    start: Nao,
    sortby,
    sortorder,
    upperbound,
    isConsumerApp,
    isCustomerIdentified: isLocalized ? isCustomerIdentified : false,
    shouldUpdateCustomContextProp: false
  };
  const response = useProductResults(opts);

  const {
    data, error, loading, variables, responseLoading
  } = response;

  const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });

  useEffect(() => {
    if (isClientSearchModelResolved) {
      setInGridBannerIndex(2);
    }
  }, [isClientSearchModelResolved]);

  const isBuyitagainEnabled = useConfigService('fs-prop:isBuyitagainEnabled');
  const cleanedPath = (pathname || '').replace(/render-\w+\//, '');
  if (!pageLoadVariables.current) {
    pageLoadVariables.current = variables;
    setDefaultVariables(variables);
  }
  if (data && !loading && !response?.responseLoading && isLocalized) {
    localizedAndClientLoaded.current = true;
    loadedStore.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode,
    };
  }
  const isCategory = (data?.searchModel?.metadata?.contentType
    && data.searchModel.metadata.contentType === 'categorypage');
  const areAppliedRefinementsAvailable = (data?.searchModel?.appliedDimensions
    && data.searchModel.appliedDimensions.length > 0);

  const isMobileOrConsumerApp = isConsumerApp || channel === 'mobile';

  const products = limitProductsToDisplay({
    products: data?.searchModel?.products,
    setLimit: isServer && isSearch,
    limitCount: SERVER_SIDE_PRODUCT_POD_RENDER_COUNT
  });

  const stringifyItemIds = (hookData) => {
    return hookData?.searchModel?.products?.map(({ itemId: sku }) => sku)
      .join('-');
  };

  /*
    Get Sample Products
  */
  const { loading: samplesLoading } = usePLPSamples({
    products,
    productsFinishedLoading: searchNavPropsRef.current.pageSize === CLIENT_SIDE_PRODUCT_POD_COUNT
      && !response.responseLoading,
    storeId,
    storeZip
  });
  // END: Get Sample Products

  const stringifyAppliedRefinements = (hookData) => {
    const appliedDimensions = hookData?.searchModel?.appliedDimensions || [];
    return appliedDimensions.map(({ refinements = [] }) => {
      return refinements.map((refinement) => refinement.refinementKey).join('-');
    }).join('-');
  };

  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const onSelectedForCompare = (id, selected = false) => {
    if (selected) {
      setSelectedForCompare([...selectedForCompare, id]);
    } else {
      setSelectedForCompare(selectedForCompare.filter((item) => item !== id));
    }
  };

  const onCompare = () => {
    const queryparams = selectedForCompare
      .map((item, idx) => `item${idx + 1}=${item}`)
      .join('&');

    navigate(`/compare?${queryparams}`, { state: { bypassExperienceCheck: true } });
  };

  const hasInStoreFilter = (resultsData) => {
    const { searchModel } = resultsData || {};
    const { appliedDimensions } = searchModel || {};
    const getItFast = (appliedDimensions || [])
      .filter((dimension) => (dimension.label || '').toUpperCase() === 'GET IT FAST');
    if (!getItFast.length) {
      return false;
    }
    const storePickUp = getItFast
      .find((dimension) => dimension.refinements
        .find((refinement) => refinement.label === 'Pick Up Today'));
    return !!storePickUp;
  };

  useEffect(() => {
    const itemIds = window.sessionStorage.getItem('compare');
    window.sessionStorage.removeItem('compare');
    if (itemIds) {
      setSelectedForCompare(JSON.parse(itemIds));
    }
  }, []);

  const resolved = isClientResolved({ queryName: 'searchModel' });
  useEffect(() => {
    if (data && !loading && resolved) {
      const analyticsSearchModel = {
        ...data?.searchModel || { searchReport: { ...searchNavPropsRef.current, startIndex: Nao } },
        defaultPageSize: searchNavPropsRef.current.pageSize,
        pageID: isNIncluded(nValue) ? nValue : 'N-' + nValue,
        isHybridContentful: isHybridLayout
      };
      if (isBrowse) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('browse.new-products', analyticsSearchModel);
      } else if (isSearch) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search.new-products', analyticsSearchModel);
      }
    }
  }, [stringifyItemIds(data), stringifyAppliedRefinements(data), resolved]);

  const resultsColProps = '';

  const mountedFn = ({ mounted }) => {
    if (!store.isLocalized) return false;
    if (response?.responseLoading) return false;
    return mounted;
  };

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') return skip;
    const isResolved = isClientResolved({ queryName: 'searchModel' });
    if (attributes.product.fulfillment) {
      if (isResolved) return skip;
      return true;
    }
    return skip;
  };

  const isInStoreFilterON = hasInStoreFilter(data);
  const isEtchVisNavInPLP = isEtchVisNavOnPLP(data);
  const storeSkuIds = resolved ? products?.map((product) => product.identifiers?.storeSkuNumber)
    .filter((sku) => !!sku) : [];
  const isPLPSearchPage = (isSearch || isBrowse);
  isNoResultsFoundRef.current = isSearch
    && data?.searchModel?.searchReport?.totalProducts === 0
    && !!keyword;
  const searchKeyword = data?.searchModel?.searchReport?.keyword;
  const maxNumOfPodsToHydrate = 12;

  const productsReduce = (productPods) => {
    let groupOfPods = 0;

    let reduced = productPods?.reduce((hydrator, product) => {
      if (!hydrator[groupOfPods]) {
        hydrator.push([]);
      }
      if (hydrator[groupOfPods].length < maxNumOfPodsToHydrate) {
        hydrator[groupOfPods].push(product);
        if (hydrator[groupOfPods].length === maxNumOfPodsToHydrate) {
          groupOfPods += 1;
        }
      }
      return hydrator;
    }, []);

    return reduced;
  };

  const originalProducts = useRef(productsReduce(products));
  const [isHydrated, setIsHydrated] = useState(false);
  const hydrators = isHydrated || !originalProducts.current?.length
    ? productsReduce(products)
    : originalProducts.current;

  setDefaultVariables({
    ...variables
  });

  if (isNoResultsFoundRef.current) {
    return (
      <>
        <TrackOrder keyword={keyword} />
        <NoResultsFound
          keyword={keyword}
          requestKey={RECS_REQUEST_KEY}
          searchData={data}
          searchError={error}
          searchLoading={loading}
          storeId={loadedStore.current?.storeId}
          isCategory={isCategory}
          pageType={pageType}
        />
      </>
    );
  }

  const hasDeliveryFilter = (breadCrumbs, filterLabelList) => {
    return (breadCrumbs || []).filter((breadCrumbDetail) => filterLabelList.includes(breadCrumbDetail?.label));
  };

  const applicableFilters = ['Same-Day Delivery', 'Next-Day Delivery', 'Free 1-2 Day Delivery'];
  const hasSameDayFilter = !!(hasDeliveryFilter(data?.searchModel?.taxonomy?.breadCrumbs, applicableFilters).length);

  return (
    <>
      <ErrorBoundary name="browse-search-redirector">
        <BrowseSearchRedirector variables={response?.variables} />
      </ErrorBoundary>
      <div className="sui-max-w-screen-2xl sui-m-auto">
        <ErrorBoundary name="browse-and-search-metadata">
          <Metadata>
            <BrowseSearchMetadata
              data={data}
              loading={loading}
              error={error}
              variables={response?.variables}
              seoGeneratedContentVariables={{ navParam: response?.variables.navParam }}
              pathWithQueryParams={path}
            />
          </Metadata>
        </ErrorBoundary>
        <div className="sui-grid sui-grid-cols-12 sui-gap-x-4 sm:sui-gap-x-8 sui-mx-4 sm:sui-mx-8">
          <div className="sui-col-span-12">
            <ErrorBoundary name="track-order">
              <TrackOrder keyword={keyword} />
            </ErrorBoundary>
            <ErrorBoundary name="breadcrumbs">
              <div className="sui-py-3 empty:sui-py-0">
                <Breadcrumbs
                  data={data}
                  loading={loading}
                  error={error}
                  hideOnNoCategory={hideOnNoCategory()}
                  useStencilDesign
                />
              </div>
            </ErrorBoundary>
            <ErrorBoundary name="connected-translator-widget">
              <div className="sui-items-center sui-flex sui-justify-end sui-ml-auto sui-mr-4">
                <ConnectedTranslatorWidget
                  data={data}
                  loading={loading}
                  keyword={keyword}
                  navParam={nValue}
                  pageType={pageType}
                />
              </div>
            </ErrorBoundary>
            <ErrorBoundary name="rental-intent">
              <RentalIntentMobile data={data} loading={loading} error={error} keyword={keyword} />
            </ErrorBoundary>
          </div>
          <ErrorBoundary name="product-results-group">
            <ProductResults data={data} loading={loading} error={error}>
              <div className="sui-col-span-12">
                <ErrorBoundary name="sponsored-banner-nucleus">
                  <div className="sui-py-3 empty:sui-py-0">
                    <SponsoredBanner
                      browserId={thdCustomer?.mcvisID || ''}
                      pageContext={{
                        label: 'browse-search',
                        data,
                        keyword,
                        isCategory,
                        isBrowse,
                        isSearch
                      }}
                      avoidPreload={false}
                    />
                  </div>
                </ErrorBoundary>
                <ErrorBoundary name="results-header">
                  <div className="sui-w-full">
                    <ResultsHeader
                      resultsRef={resultsDimensionsRef}
                    />
                  </div>
                </ErrorBoundary>
                {isHybridLayout && !hybridLoading && isBrowse
                  && (
                    <ErrorBoundary name="contentful-widgets">
                      <HybridContentfulWidgets
                        contentElements={contentElements}
                        loading={hybridLoading}
                        isBrowse={isBrowse}
                      />
                    </ErrorBoundary>
                  )}
                {!isEtchVisNavInPLP && viewVizNavFilters && !hybridLoading && !isBrandPage && data && (
                  <ErrorBoundary name="visual-nav">
                    <div className="sui--mr-4 sm:sui-mx-0 sui-pt-2 sui-overflow-auto empty:sui-py-0">
                      <ProductCategoryVisnav data={data} />
                    </div>
                  </ErrorBoundary>
                )}
              </div>
              <div className="sui-col-span-12 sui-py-4 sui-relative">
                {isBuyitagainEnabled && (
                  <ErrorBoundary>
                    <BuyItAgain
                      nValue={nValue}
                      schemaName="buyitagain"
                      anchorId={`N-${nValue}`}
                      loading={responseLoading}
                      data={data}
                      error={error}
                      showLoading
                    />
                  </ErrorBoundary>
                )}
                {!isCategory && (
                  <>
                    {!viewPrimaryFilters && isGetItFastOnPlp && (
                      <div className="sui-grid sui-grid-cols-12 sui-py-3">
                        <ErrorBoundary name="results-applied">
                          <div className="sui-col-span-12 sm:sui-col-span-9">
                            <ResultsApplied
                              onAppliedChange={onDimensionsChange}
                              isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                              isGetItFastOnPlp={isGetItFastOnPlp}
                            />
                          </div>
                        </ErrorBoundary>
                      </div>
                    )}
                    {viewPrimaryFilters && isGetItFastOnPlp && (
                      <>
                        <div className="sui-grid sui-grid-cols-12 sui-py-3">
                          <ErrorBoundary name="results-applied">
                            <div className="sui-col-span-12 sm:sui-col-span-9">
                              <ResultsApplied
                                onAppliedChange={onDimensionsChange}
                                viewPrimaryFilters={viewPrimaryFilters}
                                isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                isGetItFastOnPlp={isGetItFastOnPlp}
                              />
                            </div>
                          </ErrorBoundary>
                        </div>
                      </>
                    )}
                    {!viewPrimaryFilters && (
                      <ErrorBoundary name="results-filter-and-sort">
                        <div className="sui-p-4 sui-pr-0">
                          <ErrorBoundary name="mb-filter-and-sort">
                            <ResultsFilterAndSort
                              onSortChange={onSortChange}
                              onDimensionsChange={onDimensionsChange}
                              stickyOffset={HEADER_OFFSET}
                              enableMultiStore={!isConsumerApp}
                              disableStickyFilter
                              excludePickUpToday
                              isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                            />
                          </ErrorBoundary>
                        </div>
                      </ErrorBoundary>
                    )}
                    {viewPrimaryFilters && (
                      <>
                        {!swapPrimaryFiltersGetItFastEnabled ? (
                          <ErrorBoundary name="results-filter-and-sort-drawer">
                            <div className="sui-py-4 sui--mr-4 sm:sui-pr-0 landscape:sui-pr-0">
                              <ErrorBoundary name="mb-filter-and-sort-drawer">
                                <ResultsFilterAndSortDrawer
                                  onSortChange={onSortChange}
                                  onDimensionsChange={onDimensionsChange}
                                  stickyOffset={HEADER_OFFSET}
                                  enableMultiStore={!isConsumerApp}
                                  disableStickyFilter
                                  excludePickUpToday
                                  hideViewAll={!viewPrimaryFilters}
                                  isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                  viewPrimaryFilters={viewPrimaryFilters}
                                  isGetItFastOnPlp={isGetItFastOnPlp}
                                />
                              </ErrorBoundary>
                            </div>
                          </ErrorBoundary>
                        )
                          : (
                            <>
                              {(isFulfillmentRedesignEnabled || isGetItFastOnPlp) && (
                                !hideFFMFilters && (
                                  <div className="sui-py-2 sui--mr-4">
                                    <ResultsDimensions
                                      onDimensionsChange={onDimensionsChange}
                                      enableMultiStore
                                      showOnlyGetItFast
                                      isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                      viewPrimaryFilters={viewPrimaryFilters}
                                      isGetItFastOnPlp={isGetItFastOnPlp}
                                    />
                                  </div>
                                )
                              )}
                            </>
                          )}
                      </>
                    )}
                  </>
                )}
                {!isCategory && (
                  <ErrorBoundary
                    name="results-dimensions"
                  >

                    {!swapPrimaryFiltersGetItFastEnabled ? (
                      <>
                        {(isFulfillmentRedesignEnabled || isGetItFastOnPlp) && (
                          !hideFFMFilters && (
                            <div className="sui-py-4 sui--mr-4">
                              <ResultsDimensions
                                onDimensionsChange={onDimensionsChange}
                                enableMultiStore
                                showOnlyGetItFast
                                isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                viewPrimaryFilters={viewPrimaryFilters}
                                isGetItFastOnPlp={isGetItFastOnPlp}
                              />
                            </div>
                          )
                        )}
                      </>
                    )
                      : (
                        <ErrorBoundary name="results-filter-and-sort-drawer">
                          <div className="sui-pt-4 sui-pb-5 sui--mr-4 sm:sui-pr-0 landscape:sui-pr-0">
                            <ErrorBoundary name="mb-filter-and-sort-drawer">
                              <ResultsFilterAndSortDrawer
                                onSortChange={onSortChange}
                                onDimensionsChange={onDimensionsChange}
                                stickyOffset={HEADER_OFFSET}
                                enableMultiStore={!isConsumerApp}
                                disableStickyFilter
                                excludePickUpToday
                                hideViewAll={!viewPrimaryFilters}
                                isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                viewPrimaryFilters={viewPrimaryFilters}
                                isGetItFastOnPlp={isGetItFastOnPlp}
                              />
                            </ErrorBoundary>
                          </div>
                        </ErrorBoundary>
                      )}

                    {resolved && hasInStoreFilter(data) && (
                      <div className="sui-pb-4">
                        <ErrorBoundary name="aisle-bay-most-of-these">
                          <AisleBay products={products} searchKeyword={searchKeyword} />
                        </ErrorBoundary>
                      </div>
                    )}
                    <ErrorBoundary name="flooring-picker">
                      <FlooringDrawer products={products} />
                    </ErrorBoundary>
                    {viewPrimaryFilters && (
                      <>
                        {areAppliedRefinementsAvailable && (
                          <div className="sui-grid sui-grid-cols-12">
                            <ErrorBoundary name="results-applied">
                              <div className="sui-pb-5 sui-col-span-12 sm:sui-col-span-9 empty:sui-py-0">
                                <ResultsApplied
                                  onAppliedChange={onDimensionsChange}
                                  hideResultsCount={viewPrimaryFilters}
                                  isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                  isGetItFastOnPlp={isGetItFastOnPlp}
                                />
                              </div>
                            </ErrorBoundary>
                          </div>
                        )}
                        {!isGetItFastOnPlp && (
                          <div className="sui-grid sui-grid-cols-12">
                            <ErrorBoundary name="results-applied">
                              <div className="sui-py-3 sui-col-span-12 sm:sui-col-span-9">
                                <ResultsApplied
                                  onAppliedChange={onDimensionsChange}
                                  viewPrimaryFilters={viewPrimaryFilters}
                                  isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                  isGetItFastOnPlp={isGetItFastOnPlp}
                                />
                              </div>
                            </ErrorBoundary>
                          </div>
                        )}
                      </>
                    )}
                    {isFulfillmentRedesignEnabled && (
                      !hideFFMFilters && (
                        <div className="sui-py-4 sui--mr-4">
                          <ResultsDimensions
                            onDimensionsChange={onDimensionsChange}
                            enableMultiStore
                            showOnlyGetItFast
                            isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                          />
                        </div>
                      )
                    )}
                    <ErrorBoundary name="wrapped-results">
                      <div className="sm:sui--mx-1">
                        <ResultsWrapped>
                          <div className="sui-grid sui-w-full" ref={resultsDimensionsRef}>
                            {!products?.length && loading && isSearch && (
                              <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_RENDER_COUNT} />
                            )}
                            {!!hydrators?.length && (
                              <AisleBayPodsDataProvider
                                storeSkuIds={storeSkuIds}
                                hasInStoreFilter={hasInStoreFilter(data)}
                                isSearchPage={isPLPSearchPage}
                              >
                                <QueryProvider
                                  dataSource="searchNav"
                                  cacheKey="search-product-pods-plp-mw"
                                  defaultVariables={{
                                    storeId: loadedStore.current?.storeId,
                                    skipKPF: !showKeyProductFeaturePods,
                                    skipSubscribeAndSave: true,
                                    skipInstallServices: false,
                                    isBrandPricingPolicyCompliant: localizedAndClientLoaded.current
                                      ? isCustomerIdentified : false
                                  }}
                                  mounted={mountedFn}
                                  skip={skipFn}
                                >
                                  {hydrators?.map((group, hydratorIndex) => {
                                    if (!group?.length) return null;
                                    return (
                                      <Hydrator
                                        afterHydrate={() => { setIsHydrated(true); }}
                                        id={`browse-search-pods-${hydratorIndex + 1}`}
                                        className="sui-grid sui-grid-cols-1 sm:sui-grid-cols-2
                                        lg:sui-grid-cols-3 xl:sui-grid-cols-4"
                                        scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                                        patch={!hydratorIndex}
                                        key={`hydrator-${hydratorIndex}`}
                                      >
                                        <HoistedProductPodGroup
                                          DetailsPod={CustomProductPodDetails}
                                          keyword={keyword}
                                          detailsPodClassName="sui-bg-primary
                                          sui-mb-1
                                          sm:sui-col-span-2
                                            lg:sui-col-span-3
                                            xl:sui-col-span-4"
                                        >
                                          {group?.map((product, itemIndex) => {
                                            const { itemId, canonicalUrl, parentId } = product?.identifiers || {};
                                            const { minimumOrderQuantity } = product?.info || {};
                                            if (!itemId) {
                                              return (
                                                <div
                                                  className="sui-grid sui-py-1 sm:sui-p-1"
                                                  key={`placeholder-${itemIndex}`}

                                                >
                                                  <Skeleton grow>
                                                    <SkeletonBlock aspect="square" />
                                                    <SkeletonLine variant="multi" numberOfLines={7} />
                                                  </Skeleton>
                                                </div>
                                              );
                                            }
                                            const podPosition = hydratorIndex * maxNumOfPodsToHydrate + itemIndex;
                                            const showContentCard = (
                                              (hydratorIndex * maxNumOfPodsToHydrate) + itemIndex === 11
                                            );

                                            const showInGridBanner = (
                                              (hydratorIndex * maxNumOfPodsToHydrate) + itemIndex
                                              === inGridBannerIndex
                                            );
                                            // display discovery zone after 4th
                                            const showDiscoveryZone1 = (
                                              ((hydratorIndex * maxNumOfPodsToHydrate) + itemIndex === 3)
                                            );

                                            // display discoveryzone2/sponsoredContentCarousel after 8th product pod.
                                            const showDiscoveryZone2 = (
                                              ((hydratorIndex * maxNumOfPodsToHydrate) + itemIndex === 7)
                                            );
                                            return (
                                              <ErrorBoundary
                                                index={itemIndex}
                                                key={`${itemId}-${itemIndex}`}
                                                id={`plp-product-pod__${itemIndex}`}
                                                name={`Product Pod: ${itemIndex}`}
                                              >
                                                <div
                                                  className="sui-grid sui-py-1 sm:sui-p-1"
                                                >
                                                  <ImpressionProvider
                                                    key={itemId}
                                                    data={impProductPodData(layoutID)}
                                                  >
                                                    <CustomProductPodResponsive
                                                      parent="plp"
                                                      itemId={itemId}
                                                      parentId={parentId}
                                                      url={canonicalUrl}
                                                      storeId={loadedStore.current?.storeId}
                                                      hoverDelay={productPodHoverDelay}
                                                      position={podPosition}
                                                      itemRecsIndex={itemIndex}
                                                      samplesLoading={samplesLoading}
                                                      hasInStoreFilter={isInStoreFilterON}
                                                      impProductPodData={impProductPodData(layoutID)}
                                                      showProjectQuantity
                                                      hasSameDayFilter={hasSameDayFilter}
                                                      isMobileOrConsumerApp={isMobileOrConsumerApp}
                                                      isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
                                                      minimumOrderQuantity={minimumOrderQuantity}
                                                      enablePlccPlpComponent={enablePlccPlpComponent}
                                                      showHideViewSimilarIcon={showHideViewSimilarIcon ? {
                                                        products,
                                                        fsisViewSimilarButtonPositionAboveABTest,
                                                        fsisViewSimilarButtonPositionBelowABTest
                                                      }
                                                        : {}}
                                                    />
                                                  </ImpressionProvider>
                                                </div>
                                                {showInGridBanner
                                                  && isInGridBannerEnabled && (
                                                  <ErrorBoundary name="sponsored-ingrid-nucleus">
                                                    <div
                                                      className="sui-col-span-1 sm:sui-col-span-2 lg:sui-col-span-3
                                                      xl:sui-col-span-4 sui-pr-0 sui-pt-0 sui-mb-1 empty:sui-mb-0
                                                      sui-pl-2"
                                                    >
                                                      <SponsoredInGridBanner
                                                        browserId={thdCustomer?.mcvisID}
                                                        pageContext={{
                                                          label: 'browse-search',
                                                          data,
                                                          keyword,
                                                          isCategory,
                                                          isBrowse,
                                                          isSearch,
                                                        }}
                                                      />
                                                    </div>
                                                  </ErrorBoundary>
                                                )}
                                                {showContentCard
                                                  && (
                                                    <DynamicComponent pageType={pageType}>
                                                      <Hydrator
                                                        id="category-cards-mobile"
                                                        className="sui-grid sui-my-2 empty:sui-my-0 sui-col-span-1
                                                        sm:sui-col-span-2 lg:sui-col-span-3 xl:sui-col-span-4"
                                                      >
                                                        <ErrorBoundary>
                                                          <CategoryCardsMobile
                                                            data={data}
                                                            error={error}
                                                            loading={loading}
                                                            keyword={keyword}
                                                            navParam={nValue}
                                                          />
                                                        </ErrorBoundary>
                                                      </Hydrator>
                                                    </DynamicComponent>
                                                  )}

                                                {isDiscoveryZonesEligible
                                                  && showDiscoveryZone1
                                                  && (
                                                    <div
                                                      className="sm:sui-mx-1 sui-grid sui-col-span-1
                                                    sm:sui-col-span-2 lg:sui-col-span-3 xl:sui-col-span-4"
                                                    >
                                                      <DiscoveryZonesWrapper
                                                        data={data}
                                                        loading={loading}
                                                        nValue={nValue}
                                                        schemaName="discoveryZones"
                                                        storeId={loadedStore.current?.storeId}
                                                        zoneId="1"
                                                        errorBoundary
                                                        hydrator={{
                                                          className: `sui-bg-primary sui-my-1
                                                          empty:sui-my-0
                                                          sui-grid sui-w-full`,
                                                          delay: 1500,
                                                          scrollBuffer: 250,
                                                          id: 'discovery-zones-1'
                                                        }}
                                                        dynamic={{
                                                          pageType
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                                {((isBrowse && isDiscoveryZonesEnabled)
                                                  || (isSearch && isDiscoveryZonesSearchEnabled))
                                                  && showDiscoveryZone2
                                                  && (
                                                    <div
                                                      className="sm:sui-mx-1 sui-grid sui-col-span-1
                                                    sm:sui-col-span-2 lg:sui-col-span-3 xl:sui-col-span-4"
                                                    >
                                                      <DiscoveryZonesWrapper
                                                        data={data}
                                                        loading={loading}
                                                        nValue={nValue}
                                                        schemaName="discoveryZones"
                                                        storeId={loadedStore.current?.storeId}
                                                        zoneId="2"
                                                        errorBoundary
                                                        hydrator={{
                                                          className: `sui-bg-primary sui-grid
                                                          sui-my-1 empty:sui-my-0`,
                                                          delay: 1500,
                                                          scrollBuffer: 500,
                                                          id: 'discovery-zones-2',
                                                        }}
                                                        dynamic={{
                                                          pageType
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                              </ErrorBoundary>
                                            );
                                          })}
                                        </HoistedProductPodGroup>
                                      </Hydrator>
                                    );
                                  })}
                                </QueryProvider>
                              </AisleBayPodsDataProvider>
                            )}
                            {products?.length < 3 && isInGridBannerEnabled
                              && isClientSearchModelResolved
                              && (
                                <ErrorBoundary name="sponsored-ingrid-nucleus">
                                  <div className="sui-grid sui-grid-cols-1 sui-col-span-1 sm:sui-col-span-2
                                  lg:sui-col-span-3 xl:sui-col-span-4 sui-pr-0 sui-pt-0 sui-mb-1"
                                  >
                                    <SponsoredInGridBanner
                                      browserId={thdCustomer?.mcvisID}
                                      pageContext={{
                                        label: 'browse-search',
                                        data,
                                        keyword,
                                        isCategory,
                                        isBrowse,
                                        isSearch,
                                      }}
                                    />
                                  </div>
                                </ErrorBoundary>
                              )}
                          </div>
                        </ResultsWrapped>
                      </div>
                    </ErrorBoundary>
                    <DynamicComponent pageType={pageType}>
                      <Hydrator
                        id="text-an-expert"
                        scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      >
                        <div className="sui--mx-2">
                          <ErrorBoundary name="text-an-expert">
                            <TextAnExpert />
                          </ErrorBoundary>
                        </div>
                      </Hydrator>
                    </DynamicComponent>
                    <DynamicComponent pageType={pageType}>
                      <Hydrator
                        id="compare-tray"
                        scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      >
                        <div className="sui-w-auto sui--mx-2 sui-py-2 empty:sui-py-0">
                          <ErrorBoundary name="compare">
                            <CompareTray
                              itemIds={selectedForCompare}
                              onRemove={onSelectedForCompare}
                              onCompare={onCompare}
                            />
                          </ErrorBoundary>
                        </div>
                      </Hydrator>
                    </DynamicComponent>
                    <DynamicComponent pageType={pageType}>
                      <div className="sui-grid sui-grid-cols-12 sui-py-3">
                        <ErrorBoundary name="pagination">
                          <div className="sui-col-span-12">
                            <ResultsPagination
                              pageSize={searchNavPropsRef.current.pageSize}
                              onPageChange={onPageChange}
                              path={path}
                            />
                          </div>
                        </ErrorBoundary>
                      </div>
                      <Hydrator
                        id="feedback"
                        className="sui-grid"
                        scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      />
                    </DynamicComponent>
                  </ErrorBoundary>
                )}
              </div>
            </ProductResults>
          </ErrorBoundary>
          <div className="sui-col-span-12 sui--mx-4 sm:sui-mx-0">
            {!hybridLoading && !isBrandPage && (
              <ErrorBoundary name="plp-recs-render-placeholder">
                <div className="sui-bg-primary sui-mb-4 empty:sui-mb-0">
                  <PlpRecsRenderPlaceholder
                    requestKey={RECS_REQUEST_KEY}
                    isBrowse={isBrowse}
                    keyword={keyword}
                    itemIds={getBrowseAndSearchItemIds(products)}
                    impressionData={impPLPRecs()}
                    browseNValue={filterBrowseRecs ? nValue : ''}
                  />
                </div>
              </ErrorBoundary>
            )}
            {!hybridLoading && !isBrandPage && !hideSponsoredCarousel && (
              <ErrorBoundary name="sponsored-carousel-nucleus">
                <RetailMediaCarousel
                  breadCrumbs={data?.searchModel?.taxonomy?.breadCrumbs}
                  categorySourceId={data?.searchModel?.metadata?.categoryID}
                  pageContext={{
                    label: 'browse-search',
                    data: {
                      itemIds: getBrowseAndSearchItemIds(products),
                    },
                    keyword,
                    isCategory,
                    isBrowse,
                    isSearch
                  }}
                  nValue={nValue}
                  showSponsoredCarousel
                  dynamic={{
                    pageType
                  }}
                  hydrator={{
                    className: `sui-bg-primary sui-rounded-sm sui-w-full
                    sui-max-w-full sui-overflow-hidden sui-mb-4 empty:sui-mb-0`,
                    delay: 1500,
                    scrollBuffer: CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER
                  }}
                />
              </ErrorBoundary>
            )}
            {!hybridLoading && (
              <ErrorBoundary name="plp-rv-render-placeholder">
                <div className="sui-bg-primary sui-mb-4 empty:sui-mb-0">
                  <PlpRvRenderPlaceholder
                    isCategory={isCategory}
                    isSearch={isSearch}
                  />
                </div>
              </ErrorBoundary>
            )}
          </div>
          {isBrowse && (
            <ErrorBoundary name="brand-categories">
              <div className="sui-col-span-12 sui-py-3 empty:sui-p-0">
                <BrandCategories
                  contentId={hybridParams.slug}
                  pageName="plp"
                />
              </div>
            </ErrorBoundary>
          )}
          {isSearch && (
            <div className="sui-col-span-12">
              <DynamicComponent pageType={pageType}>
                <Hydrator
                  id="search-emt-section"
                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                >
                  <ErrorBoundary
                    name="related-search"
                  >
                    <div className="sui-py-4 empty:sui-py-0">
                      <RelatedSearch
                        navParam={nValue}
                        searchData={data}
                        searchError={error}
                        searchLoading={loading}
                        storeId={loadedStore.current?.storeId}
                      />
                    </div>
                  </ErrorBoundary>
                </Hydrator>
              </DynamicComponent>
            </div>
          )}
          {!isSearch && !hybridLoading && (
            <div className="sui-col-span-12">
              <ErrorBoundary name="emt-questions-and-answers">
                <EmtQuestionsAndAnswers
                  nValue={nValue}
                  dynamic={{
                    pageType
                  }}
                />
              </ErrorBoundary>

              <DynamicComponent pageType={pageType}>
                <Hydrator
                  id="browse-emt-section"
                  waitFor={[EmtGeneratedContentLoad, EmtLinksLoad]}
                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                >
                  <ErrorBoundary>
                    <div name="emt-product-category" className="sui-py-4 empty:sui-py-0">
                      <EmtGeneratedContent type="productCategoryContent" nvalue={nValue} />
                    </div>
                  </ErrorBoundary>
                  <ErrorBoundary name="emt-related-categories">
                    <div className="sui-py-4 empty:sui-py-0">
                      <EmtGeneratedContent type="relatedCategories" nvalue={`N-${nValue}`} nopadding />
                    </div>
                  </ErrorBoundary>
                  <ErrorBoundary name="related-search">
                    <div className="sui-py-4 empty:sui-py-0">
                      <RelatedSearch
                        navParam={nValue}
                        searchData={data}
                        searchError={error}
                        searchLoading={loading}
                        storeId={loadedStore.current?.storeId}
                      />
                    </div>
                  </ErrorBoundary>
                  <ErrorBoundary name="emt-mmg">
                    <div className="sui-py-4 empty:sui-py-0">
                      <EmtLinks path={cleanedPath} />
                    </div>
                  </ErrorBoundary>
                </Hydrator>
              </DynamicComponent>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
BrowseAndSearchMobile.displayName = 'BrowseAndSearchPageMobile';

BrowseAndSearchMobile.themeProps = {
  hideSponsoredCarousel: bool,
  hideFFMFilters: bool
};

BrowseAndSearchMobile.defaultThemeProps = {
  hideSponsoredCarousel: false,
  hideFFMFilters: false
};

BrowseAndSearchMobile.propTypes = {
  pageType: string.isRequired,
  prefetchPip: string,
  productPodHoverDelay: number,
  pathname: string.isRequired,
  customerType: string,
  instance: PropTypes.shape({})
};

BrowseAndSearchMobile.defaultProps = {
  prefetchPip: null,
  productPodHoverDelay: 500,
  customerType: 'b2c',
  instance: {}
};

const ProductSearchDataModel = {
  searchModel: params({ keyword: stringType(), navParam: stringType() }).shape({
    products: params({ startIndex: numberType() }).arrayOf(CustomProductPodResponsive.dataModel.product)
  })
};

BrowseAndSearchMobile.dataModel = extend(
  {
    searchModel: params().shape({
      metadata: shapeType({
        hasPLPBanner: boolType(),
        categoryID: stringType(),
        analytics: shapeType({
          semanticTokens: stringType(),
          dynamicLCA: stringType()
        })
      }),
      products: params().arrayOf(shapeType({
        identifiers: shapeType({
          storeSkuNumber: client(stringType()),
          specialOrderSku: client(stringType())
        }),
        installServices: params({ storeId: stringType(), zipCode: stringType() }).shape({
          scheduleAMeasure: boolType(),
          gccCarpetDesignAndOrderEligible: boolType()
        }),
        // fix datamodel discrepency due to various product-pod versions
        info: shapeType({
          sponsoredMetadata: shapeType({
            sponsoredId: stringType(),
            trackSource: stringType()
          }),
          sponsoredBeacon: shapeType({
            onClickBeacons: arrayOf(stringType()),
            onViewBeacons: arrayOf(stringType())
          }),
          minimumOrderQuantity: numberType(),
        }),
      }))
    })
  },
  ResultsDimensions,
  ResultsFilterAndSort,
  ResultsFilterAndSortDrawer,
  BrowseSearchRedirector,
  BrowseSearchMetadata,
  Breadcrumbs,
  SponsoredBanner,
  ProductCategoryVisnav,
  AisleBayPodsDataProvider,
  AisleBay,
  CategoryCardsMobile,
  CompareTray,
  DiscoveryZonesWrapper,
  EmtGeneratedContentModel,
  EmtQuestionsAndAnswers,
  EmtLinksModel,
  FlooringDrawer,
  RelatedSearch,
  NoResultsFound,
  RentalIntentMobile,
  productDefaultResultsDataModel,
  TrackOrder,
  PlpRecsRenderPlaceholder,
  PlpRvRenderPlaceholder,
  OrderSample,
  HybridContentfulWidgets.dataModel,
  BuyItAgain,
  BrandCategories,
  // This must come last.
  ProductSearchDataModel
);